import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/ui/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowRight, Play, X, Megaphone, BarChart3, Target, Zap, ChevronDown } from 'lucide-react';

// Social media portfolio — YouTube Shorts only (removed unavailable Facebook videos)
const socialPortfolio = [
  { src: 'https://www.youtube.com/embed/vSM0kivw8VI', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/7T1jGgrFG3c', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/atdWmggoUM4', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/I03lYISGK2g', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/E21iHhLwbFU', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/zFvuxhKXv2Q', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/TawmDxNCT-I', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/iEEPDG-hZM4', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/aJOONtYHtq0', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/iUGeMrkEcoo', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/NFS_vvtH_Yk', aspect: 'portrait', type: 'youtube' },
  { src: 'https://www.youtube.com/embed/lsIBj2Lyq7Y', aspect: 'portrait', type: 'youtube' },
];

// Additional social media videos for "What We Deliver" section (6 videos)
const socialShowcase = [
  { src: 'https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F956013570713638%2F&show_text=false&width=267&t=0', aspect: 'portrait', type: 'facebook' },
  { src: 'https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F2008511450086129%2F&show_text=false&width=267&t=0', aspect: 'portrait', type: 'facebook' },
  { src: 'https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1834331803894342%2F&show_text=false&width=267&t=0', aspect: 'portrait', type: 'facebook' },
  { src: 'https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Fshare%2Fr%2F1CbRCvP2SR%2F&show_text=false&width=267&t=0', aspect: 'portrait', type: 'facebook' },
  { src: 'https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F26278708488485353%2F&show_text=false&width=267&t=0', aspect: 'portrait', type: 'facebook' },
  { src: 'https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F2014235672711845%2F&show_text=false&width=267&t=0', aspect: 'portrait', type: 'facebook' },
];

const getYouTubeId = (src) => {
  const match = src.match(/\/embed\/([^?]+)/);
  return match ? match[1] : null;
};

const faqItems = [
  {
    question: 'What platforms do you manage?',
    answer: 'We work across all major social platforms including Facebook, Instagram, TikTok, YouTube, and LinkedIn. Our content is optimized specifically for each platform to maximize engagement and reach.',
  },
  {
    question: 'Do you run paid ads or just create content?',
    answer: 'Both! Our packages include content creation and ad creative. Our Growth System and Performance Engine tiers include full ad creative testing, optimization, and performance reporting.',
  },
  {
    question: 'How involved do I need to be?',
    answer: 'As much or as little as you want. We handle the strategy, content creation, and posting. You just approve the content calendar each month and we take it from there.',
  },
  {
    question: 'Can I see results before committing long-term?',
    answer: 'Absolutely. All our packages are month-to-month with no long-term contracts. We earn your business every month by delivering results you can see.',
  },
];

const SocialMediaService = () => {
  const [popupItem, setPopupItem] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen relative z-[1]">
      <Header />
      <div className="pt-32 pb-20">

        {/* Hero */}
        <section className="py-16 md:py-24 relative">
          <div className="container mx-auto px-6 text-center">
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-6">Social Media</Badge>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              Scroll-Stopping<br />
              <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Social Content</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10">
              Strategic content creation and paid ad creative that stops the scroll and converts followers into customers.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold text-lg px-10 py-6 shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-105">
                Book a Discovery Call <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* What We Deliver */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-14">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                  What We <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Deliver</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { icon: <Megaphone className="w-6 h-6" />, title: 'Content Creation', desc: 'Branded graphics, reels, stories, and carousel posts tailored to your audience.' },
                  { icon: <BarChart3 className="w-6 h-6" />, title: 'Strategy & Analytics', desc: 'Data-driven strategy to grow your reach, engagement, and conversions.' },
                  { icon: <Target className="w-6 h-6" />, title: 'Paid Advertising', desc: 'Targeted ad campaigns on Facebook, Instagram, and TikTok that convert.' },
                  { icon: <Zap className="w-6 h-6" />, title: 'Community Management', desc: 'Engage with your audience authentically to build brand loyalty.' },
                ].map((item, i) => (
                  <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 hover:border-amber-500/30 transition-all">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 mb-5">
                      {item.icon}
                    </div>
                    <h3 className="text-white font-bold text-xl mb-3">{item.title}</h3>
                    <p className="text-slate-400">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Video Showcase Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto mt-14">
                {socialShowcase.map((video, index) => {
                  const videoId = video.type === 'youtube' ? getYouTubeId(video.src) : null;
                  const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
                  return (
                    <div key={index} className="cursor-pointer group" onClick={() => setPopupItem(video)}>
                      <div className="relative aspect-[9/16] bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/10">
                        {thumbnail ? (
                          <img src={thumbnail} alt={`Social ${index + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <iframe src={video.src} className="w-full h-full border-0 pointer-events-none" title={`Video ${index + 1}`} scrolling="no" allowFullScreen style={{ border: 'none', overflow: 'hidden' }} />
                        )}
                        <div className="absolute inset-0 bg-slate-950/30 group-hover:bg-slate-950/10 transition-all flex items-center justify-center">
                          <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                            <Play className="w-6 h-6 md:w-7 md:h-7 text-slate-950 ml-0.5" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Portfolio */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                Our <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Work</span>
              </h2>
              <p className="text-slate-400 text-lg max-w-xl mx-auto">
                A selection of our social media content.
              </p>
            </div>

            <div className="max-w-6xl mx-auto space-y-10">
              {/* Landscape */}
              {socialPortfolio.filter(v => v.aspect === 'landscape').length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                  {socialPortfolio.filter(v => v.aspect === 'landscape').map((video, index) => (
                    <div key={`land-${index}`} className="cursor-pointer group" onClick={() => setPopupItem(video)}>
                      <div className="relative aspect-video bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/10">
                        <iframe src={video.src} className="w-full h-full border-0 pointer-events-none" title={`Video ${index + 1}`} scrolling="no" allowFullScreen style={{ border: 'none', overflow: 'hidden' }} />
                        <div className="absolute inset-0 bg-slate-950/30 group-hover:bg-slate-950/10 transition-all flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                            <Play className="w-8 h-8 text-slate-950 ml-1" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Portrait */}
              {socialPortfolio.filter(v => v.aspect === 'portrait').length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto">
                  {socialPortfolio.filter(v => v.aspect === 'portrait').map((video, index) => {
                    const videoId = video.type === 'youtube' ? getYouTubeId(video.src) : null;
                    const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
                    return (
                      <div key={`port-${index}`} className="cursor-pointer group" onClick={() => setPopupItem(video)}>
                        <div className="relative aspect-[9/16] bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/10">
                          {thumbnail ? (
                            <img src={thumbnail} alt={`Social ${index + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          ) : (
                            <iframe src={video.src} className="w-full h-full border-0 pointer-events-none" title={`Video ${index + 1}`} scrolling="no" allowFullScreen style={{ border: 'none', overflow: 'hidden' }} />
                          )}
                          <div className="absolute inset-0 bg-slate-950/30 group-hover:bg-slate-950/10 transition-all flex items-center justify-center">
                            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                              <Play className="w-6 h-6 md:w-7 md:h-7 text-slate-950 ml-0.5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="text-center mt-10">
              <Link to="/portfolio">
                <Button variant="outline" className="border-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-400 font-bold px-8 py-4 transition-all hover:scale-105">
                  View Full Portfolio <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-14">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                  Frequently Asked <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Questions</span>
                </h2>
              </div>
              <div className="space-y-4">
                {faqItems.map((faq, index) => (
                  <div key={index} className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/30 transition-all">
                    <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-6 text-left">
                      <span className="text-white font-bold text-lg pr-4">{faq.question}</span>
                      <ChevronDown className={`w-5 h-5 text-amber-400 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-6">
                        <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-slate-900/80 to-slate-800/50 border border-amber-500/30 rounded-3xl p-12 md:p-16 shadow-2xl shadow-amber-500/10">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-6">
                Ready to <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Go Viral?</span>
              </h2>
              <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto">
                Let us handle your social media so you can focus on what you do best.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold text-lg px-10 py-6 shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-105">
                  Book a Discovery Call <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

      </div>

      {/* Video Popup */}
      {popupItem && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4" onClick={() => setPopupItem(null)}>
          <div className={`relative bg-slate-900 rounded-2xl border border-amber-500/30 shadow-2xl shadow-amber-500/10 overflow-hidden mx-4 ${popupItem.aspect === 'portrait' ? 'max-w-sm w-full' : 'max-w-4xl w-full'}`} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPopupItem(null)} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-slate-800/80 text-white hover:bg-amber-500/20 hover:text-amber-400 flex items-center justify-center transition-all">
              <X className="w-5 h-5" />
            </button>
            <div className={`w-full ${popupItem.aspect === 'portrait' ? 'aspect-[9/16] max-h-[80vh]' : 'aspect-video'}`}>
              <iframe src={popupItem.src} className="w-full h-full border-0" title="Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen scrolling="no" style={{ border: 'none', overflow: 'hidden' }} />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SocialMediaService;
